"use client";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { useState } from "react";
import Image from "next/image";

interface Card {
  title: string;
  display_title: string;
  image: {
    data: {
      attributes: {
        url: string;
      };
    };
  };
}
interface Block {
  Cards: Card[];
  title: string;
}
interface Props {
  block: Block;
}
const BenefitSlider: React.FC<Props> = ({ block }) => {
  const [currentIndex, setCurrentIndex] = useState(1);
  const baseUrl = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL || "";

  return (
    <>
      <section className="relative z-[0] overflow-hidden py-[56px] lg:py-[80px] xl:py-[100px] bg-white ">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-[calc(40%_-_15px)_calc(60%_-_15px)]  md:gap-[30px]">
            <div className={`mb-[32px] lg:mb-[32px] xl:mb-[40px] `}>
              <h2 className="md:text-left text-center font-bold text-heading-1 text-primary-text mb-[12px] ">
                {block?.title}
              </h2>
            </div>

            <div className="relative w-full">
              {/* Swiper */}
              <Swiper
                loop={true}
                modules={[Navigation, Autoplay]}
                navigation={{ prevEl: ".prevBtn", nextEl: ".nextBtn" }}
                autoplay={{ delay: 5000, disableOnInteraction: false }}
                onSlideChange={(swiper) =>
                  setCurrentIndex(swiper.realIndex + 1)
                }
              >
                {block?.Cards?.map((card, i) => (
                  <SwiperSlide key={i}>
                    <div>
                      {card?.image?.data?.attributes?.url && (
                        <Image
                          src={baseUrl + card?.image?.data?.attributes?.url}
                          alt={`slide-${i}`}
                          width={705}
                          height={528}
                          className="w-full h-[286px] md:h-[400px] xl:h-[400px] object-cover mb-[32px]"
                        />
                      )}
                      <h5 className="text-heading-5 font-bold text-primary-text mb-[16px] md:text-left text-center">
                        {card?.display_title ?? ""}
                      </h5>
                      <p className="text-subtitle-3 font-semibold text-secondary-text mb-[16px] line-clamp-3 md:text-left text-center">
                        {card?.title ?? ""}
                      </p>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>

              {/* Desktop Controls */}
              <div className="justify-center md:justify-start items-center gap-3 mt-[24px] hidden md:flex">
                <button
                  aria-label="Previous"
                  className="prevBtn p-2 border hover:border-[#0086E6] border-border opacity-70 hover:opacity-100 cursor-pointer transition-all duration-300 bg-white"
                >
                  <svg
                    className="arrow arrow--left"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M9.57 5.93L3.5 12l6.07 6.07"
                      stroke="white"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="stroke-text-button"
                    />
                    <path
                      d="M20.5 12H3.67"
                      stroke="white"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="stroke-text-button"
                    />
                  </svg>
                </button>

                <div className="pagination-indicator text-sm font-medium">
                  <span className="text-subtitle-1 font-bold text-secondary-text">
                    {currentIndex}/{block?.Cards?.length ?? 0}
                  </span>
                </div>

                <button
                  aria-label="Next"
                  className="nextBtn hover:border-[#0086E6] p-2 border border-border opacity-70 hover:opacity-100 cursor-pointer transition-all duration-300 bg-white"
                >
                  <svg
                    className="arrow arrow--right"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M14.43 5.93L20.5 12l-6.07 6.07"
                      stroke="white"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="stroke-text-button"
                    />
                    <path
                      d="M3.5 12h16.83"
                      stroke="white"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="stroke-text-button"
                    />
                  </svg>
                </button>
              </div>

              {/* Mobile Progress Bar */}
              <div className="mt-[30px] lg:mt-[15px] md:hidden flex justify-center items-center gap-2 px-12 py-6 sm:w-[67%] mx-auto relative ">
                {block?.Cards?.length > 1 &&
                  block?.Cards?.map((_, i) => (
                    <div
                      key={i}
                      className={`p-0 relative rounded overflow-hidden ${
                        currentIndex - 1 === i
                          ? "flex w-[128px] h-1 bg-[#E6F3FD]"
                          : "flex-none w-[16px] h-1 bg-[#E6F3FD]"
                      }`}
                    >
                      <span
                        className={`absolute top-0 left-0 rounded h-1 ${
                          currentIndex - 1 === i
                            ? "bg-gradient-to-r from-[#8ABC00] to-[#0086E6] transition-all duration-[5000ms] w-[128px]"
                            : "w-[16px]"
                        }`}
                      ></span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
export default BenefitSlider;
